import {CircularProgress, Dialog, DialogContent, DialogTitle, InputAdornment} from '@mui/material';
import React, {FunctionComponent, memo, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  closeModalIcon
} from '../../../../../assets/icons/icons';
import {INotionScope} from '../../../../../models/inner-models';
import {
  fetchSubItemsForNotionUser
} from '../../../../../store/actions';
import {useAppDispatch} from '../../../../../store/hooks';
import './style.scss';
import {Button} from "../../../../../components/StyledComponents";
import Collapse from '@mui/material/Collapse';
import {ChevronDown, ChevronUp} from "react-feather";
import {SearchInput} from "../../../../../styles/components/MainInputElements";
import {SearchNormal} from "iconsax-reactjs";

type ScopeDataType = {
  id: string,
  title: string,
  object: string,
  children?: ScopeDataType[]
}
const NotionScope: FunctionComponent<INotionScope> = ({
                                                        isOpen,
                                                        closeNotification,
                                                        workspaceScopeId,
                                                        modalScopeData,
                                                        workspaceId
                                                      }: INotionScope) => {
  const dispatch = useAppDispatch();
  const {t: translate} = useTranslation();
  const [workspaceScopeData, setWorkspaceScopeData] = useState<Array<ScopeDataType>>([]);
  const [workspaceScopeLoading, setWorkspaceScopeLoading] = useState<boolean>(true);
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearch] = useState<string>('');

  useEffect(() => {
    setWorkspaceScopeLoading(true);
    setSearch('');
    setOpenIds(new Set());
  }, [isOpen]);

  useEffect(() => {
    async function getAvailableBoards() {
      try {
        const scope = await dispatch(fetchSubItemsForNotionUser({workspaceScopeId, workspaceId})).unwrap();
        if (!scope || !scope.length) {
          setWorkspaceScopeData([]);
          setWorkspaceScopeLoading(false);
          return;
        }

        setWorkspaceScopeData(scope);

        setWorkspaceScopeLoading(false);
      } catch (err) {
        console.log(err);
        setWorkspaceScopeLoading(false);
      }
    }

    if (isOpen) {
      getAvailableBoards();
    }
  }, [isOpen]);


  const handleClose = useCallback(() => {
    closeNotification && closeNotification();
  }, []);

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const hasMatchingDescendants = useCallback((item: ScopeDataType, term: string): boolean => {
    const normalizedTerm = term.toLowerCase();
    return !!item.children?.some((child) =>
      child.title.toLowerCase().includes(normalizedTerm) || hasMatchingDescendants(child, term)
    );
  }, []);

  const toggleOpenItem = useCallback((itemId: string, level: number, siblingIds: string[]) => {
    setOpenIds((prev) => {
      const next = new Set(prev);

      if (next.has(itemId)) {
        next.delete(itemId);
        return next;
      }

      // Root parents behave like accordion: opening one closes other roots.
      if (level === 0) {
        siblingIds.forEach((siblingId) => {
          if (siblingId !== itemId) {
            next.delete(siblingId);
          }
        });
      }

      next.add(itemId);
      return next;
    });
  }, []);

  const renderScopeRows = useCallback((items: ScopeDataType[], level = 0) => {
    const siblingIds = items.map((item) => item.id);

    return items.map((item) => {
      const rowKey = item.id;
      const isItemOpen = openIds.has(rowKey);
      const hasChildren = !!item.children?.length;
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase());
      const hasMatchedChildren = searchTerm ? hasMatchingDescendants(item, searchTerm) : false;

      return (
        <React.Fragment key={rowKey}>
          <div
            className="form-row"
            style={{
              background: searchTerm && !matchesSearch && hasMatchedChildren ? '#DCF0FF' : '#fff',
              cursor: hasChildren ? 'pointer' : 'default'
            }}
            onClick={() => {
              if (!hasChildren) {
                return;
              }

              toggleOpenItem(rowKey, level, siblingIds);
            }}
          >
            <div
              className="scope-item-name"
              style={{ paddingLeft: `${8 + level * 28}px` }}
            >
              {item.title}
            </div>
            <div className='scope-item-type'>{item.object}</div>
            <div className='scope-item-sub'>
              {hasChildren ?
                isItemOpen ?
                  <ChevronUp/> :
                  <ChevronDown/> :
                ''
              }
            </div>
          </div>
          {hasChildren && (
            <Collapse in={isItemOpen} timeout="auto" unmountOnExit>
              {renderScopeRows(
                searchTerm
                  ? item.children!.filter((child) =>
                    child.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    hasMatchingDescendants(child, searchTerm)
                  )
                  : item.children!,
                level + 1
              )}
            </Collapse>
          )}
        </React.Fragment>
      );
    });
  }, [hasMatchingDescendants, openIds, searchTerm, toggleOpenItem]);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="notion-repo-edit-title"
        aria-describedby="notion-repo-edit-description"
        classes={{
          paper: 'add-edit-notion-modal',
        }}
      >
        <DialogTitle id="notion-repo-edit-title">
          {translate('forms.notion_workspace_scope.title')}
        </DialogTitle>
        <div className="close-btn" onClick={handleClose}>
          <img src={closeModalIcon} loading="lazy"/>
        </div>

        <DialogContent>
          <div id="notion-repo-edit-description">
              <div className="username-block">
                <div className='notion-add-select-all'>
                  <div className='title-line'>
                    {translate('forms.notion_workspace_scope.sub_data')}
                  </div>
                  <div className='desc-line'>
                    {modalScopeData.replaceAll('&#x2F;', '/')}
                  </div>
                </div>
                <div className="search-block">
                  <SearchInput
                    className="search-field"
                    type="text"
                    placeholder="Search item..."
                    value={searchTerm}
                    onChange={onSearch}
                    startAdornment={
                      <InputAdornment position="start" className="search-icon">
                        <SearchNormal color='#3B82F6'/>
                      </InputAdornment>
                    }
                  />
                </div>
              </div>

              {workspaceScopeLoading &&
                <div className="spinner-wrapper">
                  <CircularProgress/>
                </div>
              }
              {!workspaceScopeLoading &&
                <div className="scrollable-div">
                  {!!workspaceScopeData.length && (
                    <div className="form-row row-head" key='head-0'>
                      <div className="scope-item-name head">Item title</div>
                      <div className='scope-item-type head'>Item type</div>
                      <div className='scope-item-sub head'></div>
                    </div>
                  )}
                  {!!workspaceScopeData.length ?
                    workspaceScopeData.filter((item) =>
                      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      hasMatchingDescendants(item, searchTerm)
                    )?.length ?
                    renderScopeRows(
                      workspaceScopeData.filter((item) =>
                        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        hasMatchingDescendants(item, searchTerm)
                      )
                    ):
                      <div className="form-row with-overflow empty-notion-search">
                        No Search Results
                      </div> :
                    <div className="form-row with-overflow">
                      The list of backup scope not available.
                    </div>
                  }
                </div>}
          </div>
          <div className="action-buttons">
            <Button
              onClick={handleClose}
              variant="outlined"
              color="primary"
            >
              {translate('notifications.choices.close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default memo(NotionScope);
